import { matchPath } from 'react-router-dom';
import PATHS from '../../Routers/PATHS';

/**
 * Which paths a layout has to treat differently, asked of the path rather than
 * of any state.
 *
 * A flag a page raises in an effect answers one paint too late: the reader
 * would watch one layout appear and then swap. Answering from the path means
 * the layout knows before anything renders.
 */

/**
 * The auth screens — the only signed-out pages with no header and no footer.
 *
 * The exam pass link is among them: it drops a logged-out visitor on the
 * sign-in screen under its own path, so that path has to answer the same way.
 */
const AUTH_PATHS = [
    PATHS.LANDING_SIGNIN.URL,
    PATHS.LANDING_SIGNUP.URL,
    PATHS.LANDING_ACTIVATION.URL,
    PATHS.LANDING_CHECK_EMAIL.URL,
    PATHS.LANDING_FORGOT_PASSWORD.URL,
    PATHS.LANDING_RESET_PASSWORD.URL,
    PATHS.USER_FREQ_EXAM.URL,
];

export const isV2AuthRoute = (pathname) =>
    AUTH_PATHS.some(path => matchPath(pathname, { path, exact: true }));

/**
 * The two public pages from before the redesign that stay: the ones an issued
 * badge and an issued attendance certificate link to. Those links are already
 * out with students, so the pages keep their URLs.
 *
 * They have no new design, so a layout renders them under the new header and
 * footer but outside `V2Root`, whose `base.css` would reset their semantic-ui
 * layout.
 */
const LEGACY_PUBLIC_PATHS = [
    PATHS.LANDING_BADGES.URL,
    PATHS.LANDING_CERT_ATTENDANCE.URL,
];

export const isLegacyPublicRoute = (pathname) =>
    LEGACY_PUBLIC_PATHS.some(path => matchPath(pathname, { path, exact: true }));

/**
 * The signed-in pages that render inside `V2Root`.
 *
 * Every signed-in page gets the new header; this only decides what sits under
 * it. A page on the list renders whole inside `V2Root`. A page off it — the
 * exams, their reviews and corrections — renders under the header with its old
 * styles untouched, because `base.css` resets font and direction on everything
 * inside `.v2-root`.
 *
 * The four banks and a lesson's knowledge test stay off the list. Their path
 * draws the setup wizard or the start screen, then, on the same URL, the
 * running exam, which must not sit inside a root, and then its report — so the
 * wizard and the report each carry a `V2Root` of their own (see
 * `student/pages/QuestionBank`).
 */
const STUDENT_PATHS = [
    PATHS.USER_PROFILE.URL,
    PATHS.USER_SUBSCRIPTIONS.URL,
    PATHS.USER_COURSE_DETAILS.URL,
    PATHS.USER_EXAM_RESUTLS.URL,
    PATHS.USER_DASHBOARD.URL,
    PATHS.LANDING_CERTIFICATIONS_DETAILS.URL,
    PATHS.LANDING_CERTIFICATIONS.URL,
    PATHS.LANDING_CONTACTUS.URL,
    PATHS.LANDING_PRIVACY_POLICY.URL,
    PATHS.LANDING_TERMS_AND_CONDITIONS.URL,
];

export const isV2StudentRoute = (pathname) =>
    STUDENT_PATHS.some(path => matchPath(pathname, { path, exact: true }));
